import type { SessionInfo } from "../../shared/types.js";
import { GitControl } from "./GitControl.js";
import { DataCard } from "./DataCard.js";
import { LiveTasksCard } from "./LiveTasksCard.js";

/** ID of the portal slot at the top of the right sidebar. Edit-mode actions
 * (and other transient sidebar UI) render into here via createPortal. */
export const EDIT_SLOT_ID = "dw-edit-slot";

/**
 * The right rail, in three CSS states over one DOM node (styles.css, RESPONSIVE):
 *
 *   wide            a normal layout column, as always
 *   narrow          only `.dw-rail-top` shows — the page's actions, as icons,
 *                   floating at the top of the content with the ⎇ toggle
 *   narrow + open   the same node as a full drawer over the content
 *
 * One node rather than two renderings because `EDIT_SLOT_ID` is a portal target:
 * a second copy would duplicate the id and the actions would land in whichever
 * one `getElementById` picked.
 */
export function RightSidebar({
  session,
  open,
  onToggle,
}: {
  session: SessionInfo;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <aside id="dw-rail" className={open ? "dw-rside dw-open" : "dw-rside"}>
      <div className="dw-rail-top">
        <div id={EDIT_SLOT_ID} />
        {/* Narrow-only (hidden by CSS as a column): reveals the cards below. */}
        <button
          type="button"
          className="dw-rail-toggle"
          aria-expanded={open}
          aria-controls="dw-rail"
          title={open ? "close" : "branch, data & tasks"}
          onClick={onToggle}
        >
          {open ? "×" : "⎇"}
        </button>
      </div>
      <GitControl session={session} />
      <DataCard />
      <LiveTasksCard />
    </aside>
  );
}
